class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    // Tap/click outside closes the dropdown. This carries the case onFocusOut
    // deliberately ignores: on iOS, dismissing the keyboard blurs to nowhere.
    document.addEventListener('pointerdown', (event) => {
      if (this.isOpen && !this.contains(event.target)) this.close();
    });

    // The iOS keyboard shrinks the visual viewport, not window.innerHeight,
    // so re-fit the open dropdown when the keyboard slides in or out. The
    // keyboard's show/hide animation fires a stream of resize events, so
    // debounce and re-fit once after the viewport settles — the CSS
    // transition in tt-design-system carries the movement smoothly.
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', () => {
        if (!this.isOpen) return;
        clearTimeout(this.viewportResizeTimeout);
        this.viewportResizeTimeout = setTimeout(() => {
          if (this.isMobileSheet()) {
            // The mobile sheet is positioned/sized, not max-height'd —
            // re-measure its coordinates instead of the desktop path below.
            this.fitSheet();
            return;
          }
          this.resultsMaxHeight = false;
          this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
        }, 150);
      });
    }
  }

  isMobileSheet() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  fitSheet() {
    if (!this.isMobileSheet() || !this.isOpen) return;
    // The header search bar has no .field wrapper — fall back to the input
    // itself, which is the visible search bar and the correct anchor.
    const rect = (this.input.closest('.field') || this.input).getBoundingClientRect();
    // Fixed-position coordinates are resolved against the layout viewport,
    // but the iOS keyboard only shrinks the visual viewport — offsetTop +
    // height gives the true visible bottom so the sheet stops above the
    // keys instead of running underneath them.
    const viewportBottom = window.visualViewport
      ? window.visualViewport.offsetTop + window.visualViewport.height
      : window.innerHeight;
    this.predictiveSearchResults.style.top = `${rect.bottom + 8}px`;
    this.predictiveSearchResults.style.height = `${viewportBottom - rect.bottom - 16}px`;
    this.predictiveSearchResults.style.maxHeight = 'none';
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut(event) {
    // On iOS, the keyboard's "Done" button blurs to nowhere while the shopper
    // is still reading the results — keep them open. Focus moving to a real
    // element (Tab, a click on a control) still closes; taps outside are
    // handled by the document pointerdown listener in setupEventListeners.
    if (!event.relatedTarget) return;
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }

    if (event.code === 'Escape') {
      this.close(true);
      this.input.blur();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Shopify's predictive search defaults to title/type/variants/vendor and
    // ignores tags — but this store keeps size and gender in tags, so
    // "boys size 4" returned nothing while the full search page found 943.
    // Asking for the tag field too is what makes size queries work here.
    const fields = 'title,product_type,variants.title,vendor,tag';
    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search&resources[options][fields]=${encodeURIComponent(fields)}`,
      {
        signal: this.abortController.signal,
      }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    // window.innerHeight is the layout viewport, which iOS does NOT shrink
    // for the keyboard — sizing to it pushes the bottom of the dropdown (and
    // the "See all results" button) underneath the keys. The visual viewport
    // is what the shopper can actually see.
    //
    // Measure from the panel's own top edge, not a header selector — this
    // store's header is .tt-head, not Dawn's .section-header, so the old
    // lookup silently returned 0 and let the dropdown run off-screen.
    const viewport = window.visualViewport;
    const viewportBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
    const panelTop = this.predictiveSearchResults.getBoundingClientRect().top;
    // 16px breathing room so the CTA never sits flush against the edge
    this.resultsMaxHeight = Math.max(240, viewportBottom - panelTop - 16);
    return this.resultsMaxHeight;
  }

  open() {
    // Set before fitSheet() runs below — fitSheet() early-returns while
    // !isOpen, so the synchronous fit call in the mobile branch would
    // otherwise no-op every time.
    this.isOpen = true;
    if (this.isMobileSheet()) {
      // Never move the page when the bar is already visible (that jarred) —
      // but if it's off-screen (sticky header scrolled past its section),
      // reveal it first or the sheet would anchor below the fold.
      const barRect = (this.input.closest('.field') || this.input).getBoundingClientRect();
      if (barRect.top < 0 || barRect.bottom > (window.visualViewport ? window.visualViewport.height : window.innerHeight)) {
        this.input.scrollIntoView({ block: 'start', behavior: 'auto' });
      }
      this.predictiveSearchResults.classList.add('tt-sheet');
      document.body.classList.add('tt-search-open');
      if (!this.ttScrim) {
        this.ttScrim = document.createElement('div');
        this.ttScrim.className = 'tt-search-scrim';
        document.body.appendChild(this.ttScrim);
      }
      this.ttScrim.classList.add('is-on');
      // Fit immediately so the sheet never paints unpositioned (rAF doesn't
      // fire while the page is hidden/backgrounded); the rAF pass re-fits
      // after any reveal scroll above settles.
      this.fitSheet();
      requestAnimationFrame(() => this.fitSheet());
    }
    this.setAttribute('open', true);
    if (!this.isMobileSheet()) {
      // Must run after setAttribute('open', ...) above: the panel is
      // display:none until the [open] attribute is set, and
      // getResultsMaxHeight() reads the panel's own getBoundingClientRect()
      // top — that rect is all zeros while the panel is still hidden.
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    }
    this.input.setAttribute('aria-expanded', true);
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.classList.remove('tt-sheet');
    document.body.classList.remove('tt-search-open');
    if (this.ttScrim) this.ttScrim.classList.remove('is-on');
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
